import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './config/firebase';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { BrandingProvider } from './theme/BrandingProvider';

// Shell components stay eager (small, always on-screen).
import BookingNotLiveBanner from './components/BookingNotLiveBanner';
import ReportIssueButton from './components/ReportIssueButton';
import BottomNav from './components/BottomNav';

// Pages — CODE-SPLIT (Jose 2026-10-27, perf #155): each page is lazy-loaded into its OWN chunk so a
// visitor downloads only the shell + the screen they're on, not all ~20 pages. This drops the initial
// JS from a single ~1.1 MB bundle to a small shell + per-route chunks (the client-phone lag fix). All
// lazy routes render inside <Suspense fallback={<LoadingScreen/>}> below.
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const CompleteProfilePage = React.lazy(() => import('./pages/CompleteProfilePage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const ClaimAccountPage = React.lazy(() => import('./pages/ClaimAccountPage'));
const BarbersPage = React.lazy(() => import('./pages/BarbersPage'));
const EmergencyBookingPage = React.lazy(() => import('./pages/EmergencyBookingPage'));
const MyAppointmentsPage = React.lazy(() => import('./pages/MyAppointmentsPage'));
const MultiFamilyBookingPage = React.lazy(() => import('./pages/MultiFamilyBookingPage'));
const WalletPage = React.lazy(() => import('./pages/WalletPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const GiftCardPage = React.lazy(() => import('./pages/GiftCardPage'));
const WaitlistPage = React.lazy(() => import('./pages/WaitlistPage'));
const CompactBookingPage = React.lazy(() => import('./pages/CompactBookingPage'));
const ClaimOfferPage = React.lazy(() => import('./pages/ClaimOfferPage'));
const CheckInPage = React.lazy(() => import('./pages/CheckInPage'));
const AccountPage = React.lazy(() => import('./pages/AccountPage'));
const BlockPage = React.lazy(() => import('./pages/BlockPage'));

// Loading component — engraved SLIK mark on the brand gradient with a beam of light sweeping across it
// (matches the instant splash; Jose 2026-09-26: beam instead of a spinner ring).
const LoadingScreen: React.FC = () => (
  <div className="slik-splash-full" style={{ background: 'linear-gradient(135deg, #2A211A 0%, #C0824A 100%)' }}>
    <div className="slik-mark">
      <img
        src={`${process.env.PUBLIC_URL || ''}/assets/branding/shop-logo.png`}
        onError={(e) => { const t = e.currentTarget; if (!t.src.endsWith('logo512.png')) t.src = `${process.env.PUBLIC_URL || ''}/logo512.png`; }}
        alt="SLIK"
        className="slik-splash-logo"
      />
      <span className="slik-beam" />
    </div>
    <div className="slik-splash-pb">powered by <b>NEXSY</b></div>
  </div>
);

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, loading] = useAuthState(auth);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/** External redirect — guest booking + marketing live on the website (book.slikbarberlounge.com).
 *  The portal is the branded APP (logged-in-first); its old public LandingPage/GuestBookingPage
 *  were duplicates forked into public-booking and were retired. Reconciliation 2026-09-18. */
const ExternalRedirect: React.FC<{ to: string }> = ({ to }) => {
  useEffect(() => { window.location.href = to; }, [to]);
  return <LoadingScreen />;
};

/** Reset scroll to the top on every route change so a new screen never lands mid-page
 *  (customers were landing in the middle of Book Now / Family after tapping a tile). */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const App: React.FC = () => {
  return (
    <BrandingProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <BookingNotLiveBanner />
        <ReportIssueButton />
        <React.Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* App entry — the portal is the branded APP (logged-in-first). Marketing + guest
              booking live on the website (book.slikbarberlounge.com). Reconciliation 2026-09-18. */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />{/* authed→dashboard; else ProtectedRoute→/login */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/complete-profile" element={<CompleteProfilePage />} />{/* Google sign-in → collect required phone */}
          <Route path="/book" element={<ExternalRedirect to="https://book.slikbarberlounge.com/book" />} />{/* guest booking → the website */}
          <Route path="/claim/:token" element={<ClaimOfferPage />} />
          <Route path="/checkin" element={<CheckInPage />} />{/* QR self check-in — public (guests too) */}

          {/* Protected routes - require authentication */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/claim-account"
            element={
              <ProtectedRoute>
                <ClaimAccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/booking"
            element={
              <ProtectedRoute>
                <CompactBookingPage />{/* un-gated — clients walk the full funnel; the Confirm button is locked until Oct 19 (bookingGate brain) */}
              </ProtectedRoute>
            }
          />
          <Route
            path="/booking-multi"
            element={
              <ProtectedRoute>
                <MultiFamilyBookingPage />{/* un-gated so clients can SEE/manage family; booking action is blocked inside the page */}
              </ProtectedRoute>
            }
          />
          <Route
            path="/barbers"
            element={
              <ProtectedRoute>
                <BarbersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emergency"
            element={
              <ProtectedRoute>
                <EmergencyBookingPage />{/* un-gated — full funnel visible; Submit locked until Oct 19 (bookingGate brain) */}
              </ProtectedRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <ProtectedRoute>
                <MyAppointmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <ProtectedRoute>
                <WalletPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/gift-cards"
            element={
              <ProtectedRoute>
                <GiftCardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/waitlist"
            element={
              <ProtectedRoute>
                <WaitlistPage />
              </ProtectedRoute>
            }
          />
          {/* App-shell tabs (Jose 2026-09-18): Account = the hub (Profile/Settings/Sign-Out),
              Block = the marketplace placeholder. Home + Bookings reuse /dashboard + /appointments. */}
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/block"
            element={
              <ProtectedRoute>
                <BlockPage />
              </ProtectedRoute>
            }
          />
          {/* Catch-all: unknown routes → "/" → app entry (dashboard if signed in, else login) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </React.Suspense>
        {/* Persistent bottom tab bar — shows only on the shell routes when signed in. */}
        <BottomNav />
      </BrowserRouter>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        // Push toasts below the notch / Dynamic Island so they're readable on iPhone (were rendering
        // under the camera cutout → unreadable). (2026-10-07)
        containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </AuthProvider>
    </BrandingProvider>
  );
};

export default App;
